<div
  class="z-3 text-primary text-md sticky top-0 flex h-[45px] items-center justify-between border-b border-neutral-200 bg-neutral-50 px-4 py-3 font-semibold"
>
  <span>
    {{ config()?.title || 'Question History' }}
    @if (config()?.questionId) {
      - #{{ config()?.questionId }}
    }
  </span>
  <dojo-icon-button [icon]="'close'" (click)="closeDialog()" />
</div>

<as-split [direction]="'horizontal'" unit="pixel" class="h-full flex-1" [gutterSize]="1" [useTransition]="true">
  <as-split-area [size]="300" [minSize]="250" [maxSize]="500">
    <div class="flex h-full flex-col">
      <div class="flex min-h-[45px] flex-wrap items-center border-b border-neutral-200 bg-neutral-50 px-4 py-1.5">
        <div class="flex w-full items-center justify-between">
          <span class="text-primary text-sm font-semibold">Version History</span>
          <dojo-button
            [size]="'xs'"
            [fill]="isDiffMode() ? 'solid' : 'outline'"
            [customColor]="'purple'"
            (click)="toggleDiffMode()"
            [matTooltip]="isDiffMode() ? 'Exit diff mode' : 'Compare versions'"
          >
            <dojo-icon [icon]="'code-compare'" class="mr-1.5" />
            <span>{{ isDiffMode() ? 'Exit Diff' : 'Compare' }}</span>
          </dojo-button>
        </div>
        @if (isDiffMode()) {
          <div class="text-secondary mt-2 w-full text-xs">
            Select 2 versions to compare ({{ selectedVersions().length }}/2 selected)
          </div>
        }
      </div>

      <div class="flex min-h-[45px] items-center border-b border-neutral-200 bg-neutral-50 px-4 py-1">
        <div class="flex w-full items-center justify-between">
          <span class="text-secondary text-sm font-medium">Show:</span>
          <div class="flex gap-1">
            <dojo-button
              [size]="'xs'"
              [fill]="showEdits() ? 'solid' : 'outline'"
              [customColor]="'sky'"
              (click)="toggleEditFilter()"
              [matTooltip]="'Content Edits'"
            >
              <dojo-icon [icon]="'edit'" />
            </dojo-button>

            <dojo-button
              [size]="'xs'"
              [fill]="showStatusChanges() ? 'solid' : 'outline'"
              [customColor]="'green'"
              (click)="toggleStatusChangeFilter()"
              [matTooltip]="'Status Changes'"
            >
              <dojo-icon [icon]="'swap-arrows'" />
            </dojo-button>

            <dojo-button
              [size]="'xs'"
              [fill]="showReviewFeedback() ? 'solid' : 'outline'"
              [customColor]="'teal'"
              (click)="toggleReviewFeedbackFilter()"
              [matTooltip]="'Review Feedback'"
            >
              <dojo-icon [icon]="'comment'" />
            </dojo-button>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        @if (enhancedHistory(); as entries) {
          @if (entries.length > 0) {
            @for (historyEntry of entries; track historyEntry.id) {
              <div
                class="cursor-pointer border-b border-l-4 border-neutral-100 border-l-transparent py-3 pl-3 pr-4 hover:bg-neutral-50"
                [class.bg-sky-50]="!isDiffMode() && selectedVersion() === historyEntry"
                [class.!border-l-sky-500]="!isDiffMode() && selectedVersion() === historyEntry"
                [class.bg-purple-50]="isDiffMode() && selectedVersionsSet().has(historyEntry.id)"
                [class.!border-l-purple-500]="isDiffMode() && selectedVersionsSet().has(historyEntry.id)"
                (click)="selectVersion(historyEntry)"
              >
                <div class="mb-1 flex items-center gap-2">
                  <dojo-icon [icon]="iconMap[historyEntry.type]" [size]="14" class="text-sky-500" />
                  <span class="text-xs font-semibold text-sky-500">
                    {{ historyEntry.versionDisplayName }}
                  </span>
                  <span class="ml-auto text-xs text-neutral-500">
                    @if (historyEntry.user_id && state.users[historyEntry.user_id]; as user) {
                      {{ user.fullName }}
                    } @else {
                      N/A
                    }
                  </span>
                </div>

                @if (historyEntry.type === 'status_change') {
                  <p class="text-secondary mt-1 line-clamp-1 text-xs">
                    Status: {{ historyEntry.status }}
                    @if (historyEntry.status_reason) {
                      - {{ historyEntry.status_reason }}
                    }
                  </p>
                }

                <p class="text-disabled mt-1 text-xs">
                  {{ historyEntry.timestamp | date: 'short' }}
                </p>
              </div>
            }
          } @else {
            <div class="flex h-full items-center justify-center text-neutral-500">
              <div class="text-center">
                <dojo-icon [icon]="'history'" class="mb-2 text-neutral-400" [size]="32" />
                <p class="text-sm">No history available</p>
              </div>
            </div>
          }
        } @else {
          <div class="flex h-full items-center justify-center">
            <div class="text-center text-neutral-500">
              <div class="animate-pulse">Loading history...</div>
            </div>
          </div>
        }
      </div>
    </div>
  </as-split-area>

  <as-split-area [size]="'*'" [minSize]="400">
    <div class="flex h-full flex-col">
      <div
        class="text-primary flex h-[45px] items-center border-b border-neutral-200 bg-neutral-50 px-4 py-2 text-sm font-semibold"
      >
        @if (isDiffMode()) {
          @if (selectedVersions().length === 2) {
            Version Comparison
          } @else {
            Select 2 versions to compare
          }
        } @else {
          @if (selectedVersion()) {
            Version Details
          } @else {
            Select a version to view details
          }
        }
      </div>
      <div class="flex-1 overflow-y-auto p-4">
        @if (isDiffMode()) {
          @if (diffData(); as diffData) {
            <!-- Diff View -->
            <div class="flex h-full flex-col space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div class="rounded-lg border border-red-300 bg-red-50 p-3">
                  <h4 class="mb-2 text-sm font-semibold text-red-700">Older Version</h4>
                  <div class="text-xs text-red-700">
                    <div>{{ diffData.older.versionDisplayName }}</div>
                    <div>{{ diffData.older.timestamp | date: 'short' }}</div>
                  </div>
                </div>
                <div class="rounded-lg border border-green-300 bg-green-50 p-3">
                  <h4 class="mb-2 text-sm font-semibold text-green-700">Newer Version</h4>
                  <div class="text-xs text-green-700">
                    <div>{{ diffData.newer.versionDisplayName }}</div>
                    <div>{{ diffData.newer.timestamp | date: 'short' }}</div>
                  </div>
                </div>
              </div>

              <!-- Question Items Diff -->
              <div class="flex h-full flex-col rounded-lg border border-neutral-200 bg-white p-4">
                <h4 class="text-secondary mb-3 text-sm font-semibold">Question Items Comparison</h4>
                <ngx-monaco-diff-editor
                  [options]="diffOptions"
                  [originalModel]="diffOriginalModel()"
                  [modifiedModel]="diffModifiedModel()"
                  style="height: 100%; width: 100%"
                />
              </div>
            </div>
          } @else {
            <div class="text-secondary flex h-full items-center justify-center">
              <div class="text-center">
                <dojo-icon [icon]="'code-compare'" class="text-secondary mb-2" [size]="32" />
                <p class="text-sm">Select exactly 2 versions to see the comparison</p>
                <p class="mt-1 text-xs">Currently selected: {{ selectedVersions().length }}/2</p>
              </div>
            </div>
          }
        } @else {
          @if (selectedVersion(); as version) {
            <div class="mb-4">
              <div class="mb-2 flex items-center gap-2">
                <dojo-icon [icon]="iconMap[version.type]" [size]="20" class="text-sky-500" />
                <h3 class="text-lg font-semibold">{{ version.versionDisplayName }}</h3>
              </div>

              @if (version.type === 'status_change') {
                <div class="mb-4 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-700">
                  <h4 class="mb-2 font-semibold">Status Change</h4>
                  <p class="mb-0">
                    Status: <span class="font-semibold">{{ version.status || 'Unknown' }}</span>
                  </p>
                  @if (version.status_reason) {
                    <p class="mt-1">Reason: {{ version.status_reason }}</p>
                  }
                </div>
              } @else if (version.type === 'review_feedback') {
                <div class="mb-4 rounded-lg border-teal-300 bg-teal-50 p-3 text-sm text-teal-700">
                  <h4 class="mb-2 font-semibold">Review Feedback</h4>
                  <p class="mb-0">
                    Action: <span class="font-semibold">{{ version.action_type || 'Feedback' }}</span>
                  </p>
                  @if (version.feedback) {
                    <details class="mt-2">
                      <summary
                        class="cursor-pointer text-sm font-medium text-purple-700 hover:text-purple-800 [&[open]>dojo-icon]:rotate-90"
                      >
                        View Feedback Details
                      </summary>
                      <div class="mt-2 rounded bg-purple-100 p-3">
                        <pre class="max-h-64 overflow-auto whitespace-pre-wrap text-xs text-purple-700">{{
                          version.formattedFeedback
                        }}</pre>
                      </div>
                    </details>
                  }
                </div>
              }

              <div class="text-secondary mb-4 border-t pt-2 text-xs">
                <div>
                  <span class="font-semibold">User:</span>
                  @if (version.user_id && state.users[version.user_id]; as user) {
                    {{ user.fullName }} ({{ user.email }}, {{ user.id }})
                  } @else {
                    N/A
                  }
                </div>
                <div><span class="font-semibold">Time:</span> {{ version.timestamp | date: 'medium' }}</div>
              </div>
            </div>

            @if (version.question_items && version.question_items.length > 0) {
              <div class="overflow-hidden rounded-lg border border-neutral-200">
                <h4 class="text-md text-primary bg-neutral-50 p-4 font-semibold">Question Content</h4>
                <div class="p-4">
                  <dojo-question-items-preview
                    [questionItems]="version.question_items"
                    [markCalculationMethod]="state.markCalculationMethod"
                  />
                </div>
              </div>
            } @else {
              <div class="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <p class="text-secondary text-sm">No question data available for this version.</p>
              </div>
            }
          } @else {
            <div class="text-secondary flex h-full items-center justify-center">
              <div class="text-center">
                <p class="text-sm">Select a version from the left to view details</p>
              </div>
            </div>
          }
        }
      </div>
    </div>
  </as-split-area>
</as-split>
